import { CoreButton, CoreInput, CoreSelect } from '@components'
import { toastSuccess } from '@utils'
import equal from 'fast-deep-equal'
import { useAccountCreateQry } from 'mmfintech-backend-api'
import type { SelectOptions } from 'mmfintech-backend-api/types'
import { GlobalContext, isSingleItemArray, isValidArray, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { type FormEvent, useCallback, useContext, useEffect, useState } from 'react'

import './AddAccountModal.scss'

interface Props {
  currencyOptions?: string[]
  preselectedCurrency?: string
  setCreatedAccount?: (id: number) => void
}

function AddAccountModal({ setCreatedAccount, currencyOptions, preselectedCurrency }: Props) {
  const { modalHide } = useContext(GlobalContext)

  const [filteredCurrencies, setFilteredCurrencies] = useState<SelectOptions>([])

  const { currencies, formValues, createAccountError, createAccountFetching, handleSubmit } = useAccountCreateQry({
    onSuccess: (_: unknown, id: number) => {
      typeof setCreatedAccount === 'function' && setCreatedAccount(id)
      toastSuccess(
        tr('FRONTEND.ACCOUNTS.ADD_ACCOUNT.SUCCESS_TOAST_TITLE', 'Account successfully added'),
        tr('FRONTEND.ACCOUNTS.ADD_ACCOUNT.SUCCESS_TOAST_SUBTITLE', 'You can now manage your account'),
        { duration: 3000 }
      )
      modalHide()
    }
  })

  const internalSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      handleSubmit()
    },
    [handleSubmit]
  )

  useEffect(() => {
    if (isValidArray(currencies)) {
      const filtered = isValidArray(currencyOptions)
        ? currencies.filter(currency => currencyOptions.some(currencyCode => currencyCode === currency.value))
        : currencies
      setFilteredCurrencies(prev => (equal(prev, filtered) ? prev : filtered))
    }
  }, [currencyOptions])

  useEffect(() => {
    if (isValidArray(filteredCurrencies)) {
      if (preselectedCurrency) {
        formValues.setValue('currency', filteredCurrencies.find(c => c.value === preselectedCurrency).value)
      } else if (isSingleItemArray(filteredCurrencies)) {
        formValues.setValue('currency', filteredCurrencies[0].value)
      }
    }
  }, [filteredCurrencies])

  return (
    <form className='add-account-form-wrapper' onSubmit={internalSubmit} noValidate>
      <div className='add-account-form-inputs'>
        <CoreSelect
          type='currency'
          label={tr('FRONTEND.ACCOUNTS.CREATE.CURRENCY_LABEL', 'Currency')}
          options={filteredCurrencies}
          {...formValues.registerShort('currency')}
          onChange={(_: string, value: any) => formValues.setValue('currency', value?.value)}
        />
        <CoreInput
          label={tr('FRONTEND.ACCOUNTS.CREATE.NAME_LABEL', 'Account name')}
          {...formValues.registerShort('name')}
          clearable
          onChange={(_: string, value: any) => formValues.setValue('name', value)}
        />
      </div>

      <ErrorDisplay error={createAccountError} />

      <div className='add-account-form-actions'>
        <CoreButton
          fullWidth
          variation='primary-black'
          text={tr('FRONTEND.ACCOUNTS.CREATE.SUBMIT_BUTTON', 'Add')}
          size='large'
          isLoading={createAccountFetching}
          disabled={createAccountFetching}
        />
      </div>
    </form>
  )
}

export default AddAccountModal
